<script setup lang="ts">
import { onMounted, ref, h } from 'vue'
import {
  NAlert, NCard, NButton, NSpace, NDataTable, NInput, NSwitch, NSelect, NForm, NFormItem,
  NModal, NTag, NPopconfirm, type DataTableColumns,
} from 'naive-ui'
import { notifiers, type Notifier, type NotifierInput } from '../../api'
import { message } from '../../message'

const EVENT_CHOICES = [
  'version.received',
  'deploy.started', 'deploy.success', 'deploy.failed', 'deploy.rolled_back',
  'hook.failed', 'health.up', 'health.down',
].map((v) => ({ label: v, value: v }))

const PRESETS = [
  { label: 'Generic JSON (default)', value: '' },
  { label: 'Slack', value: 'slack' },
  { label: 'DingTalk markdown', value: 'dingtalk' },
  { label: 'WeChat Work markdown', value: 'wechat-work' },
] as const

const PRESET_TEMPLATES: Record<string, string> = {
  '': '',
  slack: '{"text":"[{{.Type}}] service={{.ServiceID}} {{range $k,$v := .Payload}}{{$k}}={{$v}} {{end}}"}',
  dingtalk: '{"msgtype":"markdown","markdown":{"title":"{{.Type}}","text":"**{{.Type}}** for service {{.ServiceID}}\\n\\n{{range $k,$v := .Payload}}- {{$k}}: {{$v}}\\n{{end}}"}}',
  'wechat-work': '{"msgtype":"markdown","markdown":{"content":"**{{.Type}}** for service {{.ServiceID}}\\n{{range $k,$v := .Payload}}- {{$k}}: {{$v}}\\n{{end}}"}}',
}

const rows = ref<Notifier[]>([])
const loading = ref(false)
const showEdit = ref(false)
const editing = ref<NotifierInput & { id?: number }>(empty())
const presetSel = ref('')

function empty(): NotifierInput {
  return { name: '', url: '', events: [], template: '', headers: {}, enabled: true, allow_private: false }
}

async function load() {
  loading.value = true
  try { rows.value = (await notifiers.list()) ?? [] }
  catch (e: any) { message.error(e.message) }
  finally { loading.value = false }
}
onMounted(load)

function openNew() { editing.value = empty(); presetSel.value = ''; showEdit.value = true }
function openEdit(n: Notifier) {
  editing.value = { ...n, events: n.events ?? [], headers: n.headers ?? {} }
  presetSel.value = ''
  showEdit.value = true
}

function applyPreset(v: string) {
  editing.value.template = PRESET_TEMPLATES[v] ?? ''
}

async function save() {
  try {
    const { id, ...rest } = editing.value as any
    if (id != null) {
      await notifiers.update(id, rest)
      message.success('updated')
    } else {
      await notifiers.create(rest)
      message.success('created')
    }
    showEdit.value = false
    await load()
  } catch (e: any) { message.error(e.message) }
}

async function remove(id: number) {
  try { await notifiers.delete(id); await load() }
  catch (e: any) { message.error(e.message) }
}

async function test(id: number) {
  try { await notifiers.test(id); message.success('test event sent') }
  catch (e: any) { message.error(e.message) }
}

const cols: DataTableColumns<Notifier> = [
  { title: 'Name', key: 'name' },
  { title: 'URL', key: 'url',
    render: (n) => h('span', { style: 'font-family:monospace;font-size:12px' }, n.url) },
  { title: 'Events', key: 'events',
    render: (n) => h(NSpace, { size: 'small' }, () => (n.events ?? []).map((e) => h(NTag, { size: 'small' }, () => e))) },
  { title: 'Enabled', key: 'enabled',
    render: (n) => h(NTag, { type: n.enabled ? 'success' : 'default', size: 'small' }, () => n.enabled ? 'on' : 'off') },
  {
    title: '', key: 'actions', width: 220,
    render(n) {
      return h(NSpace, { size: 'small' }, () => [
        h(NButton, { size: 'small', onClick: () => test(n.id) }, () => 'Test'),
        h(NButton, { size: 'small', onClick: () => openEdit(n) }, () => 'Edit'),
        h(NPopconfirm, { onPositiveClick: () => remove(n.id) }, {
          default: () => `Delete "${n.name}"?`,
          trigger: () => h(NButton, { size: 'small', type: 'error', secondary: true }, () => 'Delete'),
        }),
      ])
    },
  },
]
</script>

<template>
  <NAlert type="info" show-icon style="margin-bottom:16px">
    <template #header>通知器 Notifiers（事件发生时向外推送）</template>
    Notifier 监听 sm 内部事件（如部署成功/失败、健康状态变化），通过 HTTP POST 推送到你的 webhook URL（Slack、钉钉、企业微信、自建服务等）。可选预设模板，也可用 Go <code>text/template</code> 自定义 JSON 体。点击「Test」会以测试事件触发一次推送，便于验证。
  </NAlert>

  <NCard>
    <template #header>
      <NSpace justify="space-between" style="width:100%" align="center">
        <span>Notifiers</span>
        <NButton type="primary" size="small" @click="openNew">+ 新建 Notifier</NButton>
      </NSpace>
    </template>
    <NDataTable :columns="cols" :data="rows" :loading="loading" />
  </NCard>

  <NModal v-model:show="showEdit" preset="card" title="Notifier（创建/编辑）" style="max-width: 720px">
    <NForm label-placement="top">
      <NFormItem label="名称 Name（仅用于识别）">
        <NInput v-model:value="editing.name" placeholder="例如：slack-#deploys" />
      </NFormItem>
      <NFormItem label="目标 URL（接收 POST 的地址）">
        <NInput v-model:value="editing.url" placeholder="https://hooks.example.com/…" />
      </NFormItem>
      <NFormItem label="订阅事件 Events（选择哪些事件会触发此 notifier）">
        <NSelect v-model:value="editing.events" :options="EVENT_CHOICES" multiple
          placeholder="如 deploy.success / deploy.failed / health.down" />
      </NFormItem>
      <NFormItem label="预设模板 Preset（选择后会把模板填入下方文本框，可继续修改）">
        <NSelect v-model:value="presetSel" :options="(PRESETS as any)" @update:value="applyPreset" />
      </NFormItem>
      <NFormItem label="Body 模板 Template（Go text/template；留空使用默认 JSON 格式）">
        <NInput v-model:value="editing.template" type="textarea" :rows="4"
          placeholder='可用变量：{{.Type}} 事件类型 / {{.ServiceID}} 服务 ID / {{.Payload}} 事件 payload（map）' />
      </NFormItem>
      <NSpace>
        <NFormItem label="启用 Enabled（关闭后不再推送，但保留配置）">
          <NSwitch v-model:value="editing.enabled" />
        </NFormItem>
        <NFormItem label="允许内网/loopback 目标（默认拒绝以防 SSRF；自建服务请开启）">
          <NSwitch v-model:value="editing.allow_private" />
        </NFormItem>
      </NSpace>
    </NForm>
    <template #footer>
      <NSpace>
        <NButton type="primary" @click="save">保存 Save</NButton>
        <NButton @click="showEdit = false">取消 Cancel</NButton>
      </NSpace>
    </template>
  </NModal>
</template>
